import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { SectionTitle } from "./_components/SectionTitle";
import { useAPI } from "../../context/APIContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { CourseCard } from "../../components/CourseCard";
import CardSkeleton from "../../components/common/CardSkeleton";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { SearchIcon } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import { Input } from "src/components/ui/input";
import { useAuth } from "../../context/AuthContext";

export default function MainCourses() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();

  const [sortBy, setSortBy] = useState("latest");
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // -------------Courses-------------
  const { coursesItems } = useAPI();
  const { loadingCourses, courses } = coursesItems;

  // filter and sort
  const processed = useMemo(() => {
    let list = courses;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((c) => c.title[language].toLowerCase().includes(term));
    }
    return [...list].sort((a, b) => {
      if (sortBy === "latest") return b.id - a.id;
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "type") return a.type - b.type;
      return 0;
    });
  }, [courses, searchTerm, sortBy, language]);

  // Group by main category and optional subcategory
  const grouped = useMemo(() => {
    const map = {};
    processed.forEach((item) => {
      const cat = item.category;
      const sub = item.sub_category;
      const catKey =
        cat?.title?.[language] || sub?.title?.[language] || "Uncategorized";
      // Only create subKey if both exist
      const subKey = cat && sub ? sub.title[language] : "";
      if (!map[catKey]) map[catKey] = {};
      if (!map[catKey][subKey]) map[catKey][subKey] = [];
      map[catKey][subKey].push(item);
    });
    return map;
  }, [processed, language]);

  return (
    <div
      className="space-y-10 max-w-7xl mx-auto px-4 mt-14"
      dir={language === "en" ? "ltr" : "rtl"}
    >
      {/* Head */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <SectionTitle
          title={language === "en" ? "Courses" : "المناهج التعليمية"}
        />

        <div className="flex items-center gap-2">
          <button onClick={() => setShowSearch((s) => !s)} className="p-2">
            <SearchIcon className="w-5 h-5" />
          </button>
          <AnimatePresence>
            {showSearch && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 200, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <Input
                  placeholder={
                    language === "en"
                      ? "Search courses..."
                      : "ابحث في الدورات..."
                  }
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Select onValueChange={setSortBy} defaultValue={sortBy}>
            <SelectTrigger className="w-[9rem]">
              <SelectValue placeholder="Sorted By.." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest">Latest</SelectItem>
              <SelectItem value="rating">Rating</SelectItem>
              <SelectItem value="type">Type</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Adviser */}
      {!token && (
        <Card className="px-4 bg-gradient-to-r from-blue-600 to-blue-500 relative overflow-hidden">
          <div className="p-6">
            {/* Decorative elements */}
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-blue-400 opacity-20"></div>
            <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-blue-700 opacity-20"></div>

            <div className="relative flex flex-col items-center justify-center gap-4 md:flex-row">
              <div className="text-center">
                <h3 className="mb-4 text-2xl font-bold text-white">
                  اشترك الآن!
                </h3>
                <span className="font-medium text-white">
                  واحصل علي خصم يصل الي 40% علي جيمع الدورات
                </span>
              </div>

              <div className="mt-4 md:mt-0 absolute end-0">
                <Link
                  to="/register"
                  className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-white px-8 py-3 font-bold text-blue-600 shadow-md transition duration-300 ease-out hover:scale-105"
                >
                  <span className="relative">التسجيل الان</span>
                  <span className="absolute bottom-0 left-0 h-1 w-0 bg-blue-800 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Grouped Courses */}
      {loadingCourses ? (
        <CardSkeleton />
      ) : Object.entries(grouped).length !== 0 ? (
        Object.entries(grouped).map(([catName, subs]) => (
          <div key={catName} className="space-y-8">
            <h2 className="text-2xl font-bold">{catName}</h2>
            {Object.entries(subs).map(([subName, items]) => (
              <div
                key={subName}
                className={subName ? "ml-4 space-y-2" : "space-y-2"}
              >
                {subName && (
                  <h3 className="text-xl font-semibold">{subName}</h3>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item) => (
                    <CourseCard
                      key={item.id}
                      item={item}
                      btnTitle={"التسجيل الآن"}
                      onClick={() =>
                        navigate(`/course-info`, { state: { course: item } })
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))
      ) : (
        <div className="text-2xl md:text-4xl text-center font-bold h-40 flex items-center justify-center drop-shadow-md">
          لا يوجد دورات حاليا 😪
        </div>
      )}
    </div>
  );
}
